import * as Tone from 'tone';
import type { LayerType } from '../types';

/**
 * RecordingTarget - What to record
 */
export type RecordingTarget = 'main' | LayerType;

/**
 * RecordingState - Current state of a recording
 */
export interface RecordingState {
  target: RecordingTarget;
  isRecording: boolean;
  duration: number;
  blob?: Blob;
}

/**
 * AudioRecorder - Records main output and individual layers simultaneously
 * Uses MediaRecorder API with Tone.js audio routing
 */
export class AudioRecorder {
  private recorders: Map<RecordingTarget, MediaRecorder> = new Map();
  private chunks: Map<RecordingTarget, Blob[]> = new Map();
  private streams: Map<RecordingTarget, MediaStreamAudioDestinationNode> = new Map();
  private startTime = 0;
  private isRecording = false;
  
  // Callback for state changes
  private onStateChange?: (states: Map<RecordingTarget, RecordingState>) => void;
  
  // Deferred setup
  private layerMap: Map<LayerType, { getGainNode: () => Tone.Gain }> | null = null;
  private masterOutput: Tone.ToneAudioNode | null = null;
  private isSetup = false;

  constructor() {
  }

  /**
   * Setup recording destinations for layers
   * Call this after audio engine is initialized
   */
  setupLayerRecording(
    layers: Map<LayerType, { getGainNode: () => Tone.Gain }>,
    masterOutput: Tone.ToneAudioNode
  ): void {
    // Store for deferred setup (audio context may not be started yet)
    this.layerMap = layers;
    this.masterOutput = masterOutput;
  }
  
  /**
   * Actually create the recording streams (called when audio context is running)
   */
  private doSetup(): void {
    if (this.isSetup || !this.layerMap || !this.masterOutput) return;
    
    try {
      // Create stream destination for main output
      const mainDest = Tone.getContext().createMediaStreamDestination();
      this.masterOutput.connect(mainDest);
      this.streams.set('main', mainDest);

      // Create stream destinations for each layer
      this.layerMap.forEach((layer, type) => {
        const dest = Tone.getContext().createMediaStreamDestination();
        layer.getGainNode().connect(dest);
        this.streams.set(type, dest);
      });

      this.isSetup = true;
    } catch (error) {
      console.error('❌ Error setting up recording streams:', error);
    }
  }

  /**
   * Start recording all targets (main + all layers)
   */
  startRecording(): void {
    if (this.isRecording) {
      console.warn('⚠️ Already recording');
      return;
    }
    
    // Setup streams if not done yet
    if (!this.isSetup) {
      this.doSetup();
    }
    
    if (this.streams.size === 0) {
      console.error('❌ No recording streams available');
      return;
    }

    this.startTime = Date.now();
    this.isRecording = true;
    this.chunks.clear();
    this.recorders.clear();

    // Start recording each stream
    this.streams.forEach((dest, target) => {
      this.chunks.set(target, []);
      
      const recorder = new MediaRecorder(dest.stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.chunks.get(target)?.push(e.data);
        }
      };

      recorder.start(100); // Collect data every 100ms
      this.recorders.set(target, recorder);
    });

    this.emitStateChange();
  }

  /**
   * Stop recording and return all recordings
   */
  async stopRecording(): Promise<Map<RecordingTarget, Blob>> {
    if (!this.isRecording) {
      console.warn('⚠️ Not recording');
      return new Map();
    }

    this.isRecording = false;
    const results = new Map<RecordingTarget, Blob>();

    // Stop all recorders and collect blobs
    const stopPromises = Array.from(this.recorders.entries()).map(([target, recorder]) => {
      return new Promise<void>((resolve) => {
        recorder.onstop = () => {
          const chunks = this.chunks.get(target) || [];
          const blob = new Blob(chunks, { type: 'audio/webm' });
          results.set(target, blob);
          resolve();
        };
        recorder.stop();
      });
    });

    await Promise.all(stopPromises);

    this.emitStateChange();

    return results;
  }

  /**
   * Get recording state
   */
  getIsRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Get current recording duration in seconds
   */
  getRecordingDuration(): number {
    if (!this.isRecording) return 0;
    return (Date.now() - this.startTime) / 1000;
  }

  /**
   * Set state change callback
   */
  setOnStateChange(callback: (states: Map<RecordingTarget, RecordingState>) => void): void {
    this.onStateChange = callback;
  }

  /**
   * Emit state change event
   */
  private emitStateChange(): void {
    if (!this.onStateChange) return;

    const states = new Map<RecordingTarget, RecordingState>();
    this.streams.forEach((_, target) => {
      states.set(target, {
        target,
        isRecording: this.isRecording,
        duration: this.getRecordingDuration(),
        blob: undefined
      });
    });

    this.onStateChange(states);
  }

  /**
   * Download a recording as WAV file
   */
  async downloadRecording(blob: Blob, filename: string): Promise<void> {
    // Convert webm to wav for better compatibility
    const wavBlob = await this.convertToWav(blob);
    
    const url = URL.createObjectURL(wavBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Download all recordings as separate files
   */
  async downloadAllRecordings(
    recordings: Map<RecordingTarget, Blob>,
    baseName: string
  ): Promise<void> {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    
    for (const [target, blob] of recordings) {
      const filename = target === 'main' 
        ? `${baseName}_${timestamp}_main`
        : `${baseName}_${timestamp}_${target}`;
      await this.downloadRecording(blob, filename);
    }
  }

  /**
   * Convert webm blob to wav
   */
  private async convertToWav(webmBlob: Blob): Promise<Blob> {
    // Decode the webm audio
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioContext = new AudioContext();
    
    try {
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Encode as WAV
      const wavBuffer = this.encodeWav(audioBuffer);
      return new Blob([wavBuffer], { type: 'audio/wav' });
    } catch (error) {
      console.warn('⚠️ Could not convert to WAV, returning original format');
      return webmBlob;
    } finally {
      await audioContext.close();
    }
  }

  /**
   * Encode AudioBuffer as WAV
   */
  private encodeWav(audioBuffer: AudioBuffer): ArrayBuffer {
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const samples = audioBuffer.length;
    const dataSize = samples * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    
    // WAV header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Interleave channels and write samples
    const channels: Float32Array[] = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }
    
    let offset = 44;
    for (let i = 0; i < samples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }
    
    return buffer;
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.isRecording) {
      this.recorders.forEach(recorder => recorder.stop());
    }
    this.recorders.clear();
    this.chunks.clear();
    this.streams.clear();
  }
}
